import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  Tag,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Tooltip
} from 'antd';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { adminService, User, UserFilters } from '../../../services/adminService';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { Search } = Input;

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState<UserFilters>({});
  const [form] = Form.useForm();

  useEffect(() => {
    loadUsers();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminService.getUsers({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
      });

      if (response.success && response.data) {
        setUsers(response.data.users);
        setPagination(prev => ({
          ...prev,
          total: response.data?.pagination?.total || 0,
        }));
      }
    } catch (error) {
      console.error('載入用戶失敗:', error);
      message.error('載入用戶失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      total: pagination.total,
    });
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const showModal = (user?: User) => {
    setEditingUser(user || null);
    setModalVisible(true);
    
    if (user) {
      form.setFieldsValue({
        username: user.username,
        email: user.email,
        role: user.role,
        isActive: user.isActive,
        firstName: user.profile?.firstName || '',
        lastName: user.profile?.lastName || '',
        phone: user.profile?.phone || '',
      });
    } else {
      form.resetFields();
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      const userData = {
        username: values.username,
        email: values.email,
        role: values.role,
        isActive: values.isActive,
        profile: {
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
        },
      };

      let response;
      if (editingUser) {
        response = await adminService.updateUser(editingUser._id, userData);
      } else {
        const createUserData = {
          ...userData,
          password: values.password,
        };
        response = await adminService.createUser(createUserData);
      }

      if (response.success) {
        message.success(editingUser ? '用戶更新成功' : '用戶創建成功');
        setModalVisible(false);
        loadUsers();
      } else {
        message.error(response.message || '操作失敗');
      }
    } catch (error) {
      console.error('保存用戶失敗:', error);
      message.error('保存用戶失敗');
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      const response = await adminService.deleteUser(userId);
      if (response.success) {
        message.success('用戶刪除成功');
        loadUsers();
      } else {
        message.error(response.message || '刪除失敗');
      }
    } catch (error) {
      console.error('刪除用戶失敗:', error);
      message.error('刪除用戶失敗');
    }
  };

  const handleStatusToggle = async (userId: string, currentStatus: boolean) => {
    try {
      const response = await adminService.updateUserStatus(userId, !currentStatus);
      if (response.success) {
        message.success(`用戶已${!currentStatus ? '啟用' : '停用'}`);
        loadUsers();
      } else {
        message.error(response.message || '狀態更新失敗');
      }
    } catch (error) {
      console.error('更新用戶狀態失敗:', error);
      message.error('更新用戶狀態失敗');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'red';
      case 'operator': return 'blue';
      case 'user': return 'green';
      default: return 'default';
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin': return '管理員';
      case 'operator': return '操作員';
      case 'user': return '用戶';
      default: return role;
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: '用戶名',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <span>{text}</span>
          {!record.isActive && <Tag color="red">已停用</Tag>}
        </Space>
      ),
    },
    {
      title: '郵箱',
      dataIndex: 'email',
      key: 'email',
      render: (text) => (
        <Space>
          <MailOutlined />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleText(role)}
        </Tag>
      ),
    },
    {
      title: '姓名',
      key: 'fullName',
      render: (_, record) => {
        const firstName = record.profile?.firstName || '';
        const lastName = record.profile?.lastName || '';
        return firstName || lastName ? `${firstName} ${lastName}`.trim() : '-';
      },
    },
    {
      title: '電話',
      key: 'phone',
      render: (_, record) => (
        record.profile?.phone ? (
          <Space>
            <PhoneOutlined />
            <span>{record.profile.phone}</span>
          </Space>
        ) : '-'
      ),
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleStatusToggle(record._id, isActive)}
          checkedChildren="啟用"
          unCheckedChildren="停用"
        />
      ),
    },
    {
      title: '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString('zh-TW'),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="編輯用戶">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="確定要刪除此用戶嗎？"
            description="此操作無法撤銷"
            onConfirm={() => handleDelete(record._id)}
            okText="確定"
            cancelText="取消"
          >
            <Tooltip title="刪除用戶">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="user-management">
      {/* 篩選和操作區域 */}
      <Card className="filter-card" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Search
              placeholder="搜索用戶名或郵箱"
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="角色"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('role', value)}
            >
              <Option value="admin">管理員</Option>
              <Option value="operator">操作員</Option>
              <Option value="user">用戶</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="狀態"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="active">啟用</Option>
              <Option value="inactive">停用</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Space>
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={() => showModal()}
              >
                新增用戶
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadUsers}
                loading={loading}
              >
                刷新
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 用戶表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* 新增/編輯用戶模態框 */}
      <Modal
        title={editingUser ? '編輯用戶' : '新增用戶'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isActive: true,
            role: 'user',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label="用戶名"
                rules={[{ required: true, message: '請輸入用戶名' }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="郵箱"
                rules={[
                  { required: true, message: '請輸入郵箱' },
                  { type: 'email', message: '請輸入有效的郵箱地址' },
                ]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          {!editingUser && (
            <Form.Item
              name="password"
              label="密碼"
              rules={[
                { required: true, message: '請輸入密碼' },
                { min: 6, message: '密碼至少6位' },
              ]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: '請選擇角色' }]}
              >
                <Select>
                  <Option value="user">用戶</Option>
                  <Option value="operator">操作員</Option>
                  <Option value="admin">管理員</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="狀態"
                valuePropName="checked"
              >
                <Switch checkedChildren="啟用" unCheckedChildren="停用" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="firstName" label="名">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="姓">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="phone" label="電話">
            <Input prefix={<PhoneOutlined />} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;